/**
 * Helpers for work with API requests.
 *
 */

import { AxiosResponse } from 'axios';
import INSTANCE from '@/axios';
import userModule from '@/store/user';

export type Response<T> = {
    result: T,
}

export type OkResponse = Response<{
    status: 'ok',
}>

/**
 * Send request with Form data.
 *
 * @param url
 * @param method
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const makeNotAuthGetRequest = <T = any>(url: string): Promise<AxiosResponse<T>> => {
    return INSTANCE.request<T>({
        url,
        method: 'GET',
    });
};

/**
 * Send request with Form data.
 *
 * @param url
 * @param method
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const makeGetRequest = <T = any>(url: string): Promise<AxiosResponse<T>> => {
    return INSTANCE.request<T>({
        url,
        method: 'GET',
        headers: {
            Authorization: `Bearer ${userModule.token}`,
        },
    });
};

/**
 * Send request with Form data.
 *
 * @param url
 * @param formData
 * @param method
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const makeFormRequest = <T = any>(
    url: string,
    formData: { [key: string]: any }, // Разрешаем любой тип данных
    method: 'POST' | 'PUT' | 'PATCH' = 'POST',
): Promise<AxiosResponse<T>> => {
    const data = new FormData();

    // Рекурсивная функция для добавления данных в FormData
    const appendFormData = (key: string, value: any) => {
        if (value instanceof Blob || value instanceof File) {
            data.append(key, value);
        } else if (Array.isArray(value)) {
            // Обработка массивов
            value.forEach((item, index) => {
                if (typeof item === 'object' && item !== null) {
                    // Если элемент массива — объект, обрабатываем его рекурсивно
                    Object.entries(item).forEach(([subKey, subValue]) => {
                        appendFormData(`${key}[${index}][${subKey}]`, subValue);
                    });
                } else {
                    // Если элемент массива — примитив, добавляем его напрямую
                    data.append(`${key}[${index}]`, String(item));
                }
            });
        } else if (typeof value === 'object' && value !== null) {
            // Обработка вложенных объектов
            Object.entries(value).forEach(([subKey, subValue]) => {
                appendFormData(`${key}[${subKey}]`, subValue);
            });
        } else {
            // Обработка примитивов (строки, числа)
            data.append(key, String(value));
        }
    };

    // Добавляем все данные в FormData
    Object.entries(formData).forEach(([key, value]) => {
        appendFormData(key, value);
    });

    // Отправляем запрос
    return INSTANCE.request<T>({
        url,
        method,
        data,
        headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${userModule.token}`,
        },
    });
};

export const makePostRequest = <T = unknown> (
    url: string,
    data: any,
): Promise<AxiosResponse<T>> => {

    return INSTANCE.request<T>({
        url,
        method: 'POST',
        data,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userModule.token}`,
        },
    });
}

/**
 * Send request with Form data.
 *
 * @param url
 * @param method
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const makeDeleteRequest = <T = any>(url: string): Promise<AxiosResponse<T>> => {
    return INSTANCE.request<T>({
        url,
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${userModule.token}`,
        },
    });
};